import React from 'react';
import cn from 'classnames';
import { useLocation } from '@reach/router';
import Image from 'components/UI/Image';
import Title from 'components/UI/Title';
import Icon from 'components/UI/Icon';
import Link from 'components/UI/Link';
import { useLang } from 'utils/localisation';
import { pushCustomEvent } from 'utils/analytics';
import { modal as modalStore } from 'store';

import * as styles from './Card.module.scss';

export default function Card({
  image,
  title,
  subtitle,
  startDate,
  finishDate,
  location,
  url,
  theme,
  isBig = false,
  bigImage,
  global,
}) {
  const lang = useLang();
  const { pathname } = useLocation();
  const hideImagesFromAssistiveTech = pathname === '/';

  const getLocale = () => {
    if (lang === 'de') {
      return 'de-DE';
    }
    if (lang === 'ae') {
      return 'ar-AE';
    }
    return 'en-US';
  };

  const preparedDate = (date, options) =>
    new Date(date)
      .toLocaleDateString(getLocale(), {
        month: 'short',
        day: 'numeric',
        ...options,
      })
      .replaceAll('.', '');

  const preparedStartMonth = preparedDate(startDate, { day: undefined });

  const getDuration = () => {
    if (lang === 'de') {
      const isMultiDay =
        getDay(startDate) !== getDay(finishDate) || getMonth(startDate) !== getMonth(finishDate);

      if (isMultiDay) {
        const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
        const start = new Date(startDate).toLocaleDateString(getLocale(), options);
        const finish = new Date(finishDate).toLocaleDateString(getLocale(), options);

        return `von ${start} bis ${finish}`;
      }

      let singleDayEventDate = new Date(startDate).toLocaleString(getLocale(), {
        month: 'short',
        day: 'numeric',
      });

      if (singleDayEventDate[singleDayEventDate.length - 1] === '.') {
        singleDayEventDate = singleDayEventDate.slice(0, -1);
      }

      return `${singleDayEventDate} ${getYear(startDate)}`;
    }

    if (getYear(startDate) !== getYear(finishDate)) {
      return lang === 'ae'
        ? `${preparedDate(finishDate, { year: 'numeric' })} – ${preparedDate(startDate, {
            year: 'numeric',
          })}`
        : `${preparedDate(startDate, { year: 'numeric' })} – ${preparedDate(finishDate, {
            year: 'numeric',
          })}`;
    }

    if (getMonth(startDate) !== getMonth(finishDate)) {
      return lang === 'ae'
        ? `${preparedDate(finishDate)} – ${preparedDate(startDate, {
            year: 'numeric',
          })}`
        : `${preparedDate(startDate)} – ${preparedDate(finishDate, {
            year: 'numeric',
          })}`;
    }

    if (getDay(startDate) !== getDay(finishDate)) {
      return lang === 'ae'
        ? `${getDay(finishDate)} – ${getDay(startDate)} ${preparedStartMonth} ${getYear(
            finishDate,
          )}`
        : `${preparedStartMonth} ${getDay(startDate)} – ${getDay(finishDate)}, ${getYear(
            finishDate,
          )}`;
    }

    return lang === 'ae'
      ? `${getDay(startDate)} ${preparedStartMonth} ${getYear(finishDate)}`
      : `${preparedStartMonth} ${getDay(startDate)}, ${getYear(finishDate)}`;
  };

  const handleClick = e => {
    if (global) {
      const { title, subtitle } = global;
      e.preventDefault();
      modalStore.open('LangNotificationModal', { href: url, target: '_blank', title, subtitle });
    }
    pushCustomEvent('ndrsn_events_block', {
      action: 'click_on_card',
      label: title,
    });
  };

  const Wrapper = url ? Link : 'div';

  return (
    <>
      <Wrapper
        className={cn(styles.wrapper, !url && styles.disabled, isBig && styles.big, styles[theme])}
        href={url}
        target="_blank"
        onClick={handleClick}
      >
        <Image
          className={styles.picture}
          classImage={styles.image}
          src={isBig ? bigImage : image}
          aria-hidden={hideImagesFromAssistiveTech}
        />
        <div className={styles.contentWrapper}>
          <div className={styles.text}>
            <Title tag="h3" size="h4">
              {title}
            </Title>
            <p className={styles.subtitle}>{subtitle}</p>
          </div>
          <div className={styles.details}>
            <div className={styles.detailItem}>
              <Icon
                className={styles.detailIcon}
                name="checked-calendar"
                aria-hidden={hideImagesFromAssistiveTech}
              />
              <span>{getDuration()}</span>
            </div>
            <div className={styles.detailItem}>
              <Icon
                className={styles.detailIcon}
                name="marker-filled"
                aria-hidden={hideImagesFromAssistiveTech}
              />
              <span>{location}</span>
            </div>
          </div>
        </div>
      </Wrapper>
    </>
  );
}

function getYear(date) {
  return new Date(date).getFullYear();
}

function getMonth(date) {
  return new Date(date).getMonth();
}

function getDay(date) {
  return new Date(date).getDate();
}
